import React, { useEffect, useState } from "react";
import { adminUsers, adminUserSessions } from "../../adminApi";
import { Search, Eye, Loader2, Smartphone, Monitor, Tablet, MapPin, Calendar } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";

function DeviceIcon({ device }) {
  if (device === "mobile") return <Smartphone className="w-3.5 h-3.5" />;
  if (device === "tablet") return <Tablet className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
}

function fmt(d) {
  if (!d) return "-";
  return new Date(d).toLocaleString("es-DO");
}

export default function AdminUsers() {
  const [data, setData] = useState({ users: [], total: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openUser, setOpenUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await adminUsers(q);
      setData(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const openSessions = async (u) => {
    setOpenUser(u);
    setLoadingSessions(true);
    try {
      setSessions(await adminUserSessions(u.id));
    } finally {
      setLoadingSessions(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <h2 className="text-2xl font-extrabold text-gray-900">Usuarios <span className="text-sm font-normal text-gray-500">({data.total})</span></h2>
        <form onSubmit={onSearch} className="flex items-center bg-white border-2 border-gray-200 focus-within:border-[#1d6cb1] rounded-full px-3 py-1.5">
          <Search className="w-4 h-4 text-gray-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar email, nombre, c\u00f3digo..." className="flex-1 bg-transparent outline-none text-sm px-2 py-1 w-64" />
          <button className="px-3 py-1 rounded-full bg-[#1d6cb1] text-white text-xs font-semibold">Buscar</button>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Créditos</th>
                <th className="px-4 py-3 text-left font-semibold">Código Ref.</th>
                <th className="px-4 py-3 text-left font-semibold">Última conexión</th>
                <th className="px-4 py-3 text-left font-semibold">País / Disp.</th>
                <th className="px-4 py-3 text-left font-semibold">Registro</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-10 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Cargando...</td></tr>
              )}
              {!loading && data.users.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-gray-500">Sin usuarios.</td></tr>
              )}
              {data.users.map((u) => (
                <tr key={u.id} className="border-t border-gray-100 hover:bg-blue-50/30">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{u.name}{u.is_admin && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] rounded font-bold">ADMIN</span>}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 font-bold text-gray-900">{u.credits}</td>
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">{u.ref_code || "-"}</td>
                  <td className="px-4 py-3 text-xs text-gray-700">{fmt(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    <div className="flex items-center gap-1">
                      {u.last_country && <><MapPin className="w-3 h-3" />{u.last_country}</>}
                    </div>
                    <div className="flex items-center gap-1 text-gray-500 mt-0.5">
                      <DeviceIcon device={u.last_device} /> {u.last_device || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{fmt(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => openSessions(u)} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-[#1d6cb1] text-xs font-semibold hover:bg-blue-100">
                      <Eye className="w-3.5 h-3.5" /> Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!openUser} onOpenChange={(v) => !v && setOpenUser(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{openUser?.name}</SheetTitle>
          </SheetHeader>
          <p className="text-xs text-gray-500">{openUser?.email}</p>
          <h4 className="mt-5 mb-2 font-bold text-gray-800">Historial de conexiones</h4>
          {loadingSessions && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
          {!loadingSessions && sessions.length === 0 && <p className="text-sm text-gray-500">Sin registros.</p>}
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.created_at + s.ip} className="bg-blue-50/40 border border-blue-100 rounded-xl px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800 flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmt(s.created_at)}</span>
                  <span className="px-2 py-0.5 bg-white rounded-full text-[10px] font-bold uppercase text-gray-600">{s.kind}</span>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-1 text-gray-600">
                  <span><strong>IP:</strong> {s.ip}</span>
                  <span><DeviceIcon device={s.device} /> {s.device} · {s.os} · {s.browser}</span>
                  <span className="col-span-2"><MapPin className="w-3 h-3 inline" /> {[s.city, s.region, s.country].filter(Boolean).join(", ") || "Local"}</span>
                </div>
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>
    </div>
  );
}
